<template>
  <v-container fluid class="quotation-options-container">
    <!-- Header -->
    <v-card class="mb-6 header-card" elevation="8">
      <v-card-text class="pa-8">
        <v-row align="center" justify="space-between">
          <v-col cols="12" md="8">
            <h1 class="text-h3 font-weight-bold mb-2">
              <v-icon size="30" color="white">mdi-folder-multiple</v-icon>
              <span class="ml-2 text-h4 text-white font-weight-bold mb-2">Nueva Cotización con Opciones</span>
            </h1>
            <p class="text-h6 mb-0 subtitle">Crea múltiples propuestas para el mismo evento</p>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn 
              color="white" 
              variant="outlined"
              size="large"
              prepend-icon="mdi-format-list-bulleted"
              @click="goToList"
            >
              Ver Cotizaciones
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Información del Cliente -->
    <v-card class="mb-6" elevation="4">
      <v-card-title class="text-h5 pa-6 bg-grey-lighten-4">
        <v-icon start color="primary">mdi-account-multiple</v-icon>
        Información del Evento
      </v-card-title>
      <v-card-text class="pa-6">
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="tipoEventoID"
              :items="tiposEvento"
              item-title="NombreTipo"
              item-value="TipoEventoID"
              label="Tipo de Evento"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-party-popper"
              :rules="[v => !!v || 'Campo requerido']"
              @update:model-value="onTipoEventoChange"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="nombreCliente"
              label="Nombre del Cliente"
              placeholder="Ej: Juan Pérez"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-account"
              :rules="[v => !!v || 'Campo requerido']"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="eventDate"
              label="Fecha del Evento"
              type="date"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-calendar"
              :rules="[v => !!v || 'Campo requerido']"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="guestCount"
              label="Número de Invitados"
              type="number"
              placeholder="100"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-account-group"
              :rules="[v => !!v || 'Campo requerido']"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Sistema de Opciones Múltiples -->
    <v-card class="mb-6" elevation="4">
      <v-card-title class="text-h5 pa-6 bg-primary text-white">
        <v-icon start>mdi-layers-triple</v-icon>
        Opciones de Cotización
        <v-spacer></v-spacer>
        <v-btn
          color="white"
          variant="outlined"
          size="small"
          prepend-icon="mdi-plus-circle"
          @click="addOption"
        >
          Agregar Opción
        </v-btn>
        <v-btn
          color="white"
          variant="outlined"
          size="small"
          prepend-icon="mdi-refresh"
          @click.stop="loadCatalog"
          class="ml-2"
        >
          Cargar Servicios
        </v-btn>
        <v-btn
          color="white"
          variant="outlined"
          size="small"
          prepend-icon="mdi-reload"
          @click.stop="resetForm"
          class="ml-2"
        >
          Reiniciar Todo
        </v-btn>
      </v-card-title>

      <!-- Tabs para las opciones -->
      <v-tabs
        v-model="selectedOptionTab"
        bg-color="grey-lighten-4"
        color="primary"
        show-arrows
      >
        <v-tab
          v-for="(option, index) in options"
          :key="index"
          :value="index"
        >
          <v-icon start>mdi-numeric-{{ index + 1 }}-box</v-icon>
          {{ option.nombre }}
          <v-btn
            v-if="options.length > 1"
            icon="mdi-close"
            size="x-small"
            variant="text"
            color="error"
            class="ml-2"
            @click.stop="removeOption(index)"
          />
        </v-tab>
      </v-tabs>

      <!-- Contenido de cada opción -->
      <v-window v-model="selectedOptionTab">
        <v-window-item
          v-for="(option, optionIndex) in options"
          :key="optionIndex"
          :value="optionIndex"
        >
          <v-card-text class="pa-6">
            <!-- Configuración de la opción -->
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="option.nombre"
                  label="Nombre de la Opción"
                  placeholder="Ej: Decoración Completa"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-rename-box"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="option.descripcion"
                  label="Descripción (opcional)"
                  placeholder="Breve descripción de esta opción"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-text"
                />
              </v-col>
            </v-row>

            <!-- Buscador para esta opción -->
            <v-text-field
              v-model="option.search"
              label="Buscar servicios o items para esta opción"
              placeholder="Buscar..."
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-magnify"
              clearable
              class="mb-4"
              @input="changeSearchForOption(optionIndex)"
            />

            <!-- Catálogo de servicios para esta opción -->
            <v-expansion-panels 
              v-model="option.expandedPanels"
              multiple
              variant="accordion"
            >
              <v-expansion-panel
                v-for="servicio in getCatalogoFiltrado(optionIndex)"
                :key="servicio.ServicioID"
                elevation="2"
                class="mb-3"
                v-show="getLengthItemsForService(servicio.ServicioID, optionIndex) > 0"
              >
                <!-- Header del Servicio -->
                <v-expansion-panel-title class="bg-grey-lighten-5">
                  <template v-slot:default="{ expanded }">
                    <v-row no-gutters align="center">
                      <v-col cols="8" class="d-flex align-center">
                        <v-icon 
                          :color="expanded ? 'primary' : 'grey'"
                          class="mr-3"
                        >
                          {{ expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                        </v-icon>
                        <span class="text-h6 font-weight-bold">
                          {{ servicio.NombreServicio }}
                        </span>
                      </v-col>
                      <v-col cols="4" class="text-right">
                        <v-chip 
                          :color="getItemsCountForService(servicio.ServicioID, optionIndex) > 0 ? 'primary' : 'grey'"
                          variant="flat"
                          size="small"
                        >
                          <v-icon start size="small">mdi-check-circle</v-icon>
                          {{ getItemsCountForService(servicio.ServicioID, optionIndex) }} seleccionados
                        </v-chip>
                      </v-col>
                    </v-row>
                  </template>
                </v-expansion-panel-title>

                <!-- Contenido: Secciones -->
                <v-expansion-panel-text>
                  <!-- Botón seleccionar todos del servicio -->
                  <div class="mb-4">
                    <v-btn 
                      :color="getItemsCountForService(servicio.ServicioID, optionIndex) > 0 ? 'success' : 'primary'"
                      variant="outlined"
                      size="small"
                      @click="toggleAllItemsForService(servicio.ServicioID, optionIndex)"
                    >
                      <v-icon start>{{ getItemsCountForService(servicio.ServicioID, optionIndex) > 0 ? 'mdi-check-all' : 'mdi-plus-box-multiple' }}</v-icon>
                      Seleccionar Todos
                    </v-btn>
                  </div>
                  
                  <v-expansion-panels 
                    v-model="option.expandedSections[servicio.ServicioID]"
                    multiple
                  >
                    <v-expansion-panel
                      v-for="seccion in servicio.secciones"
                      :key="seccion.SeccionID"
                      elevation="1"
                      class="mb-2"
                      :disabled="seccion.items.length === 0"
                    >
                      <v-expansion-panel-title class="bg-blue-grey-lighten-5">
                        <template v-slot:default="{ expanded }">
                          <v-row no-gutters align="center">
                            <v-col cols="9" class="d-flex align-center">
                              <v-icon 
                                :color="expanded ? 'secondary' : 'grey-darken-1'"
                                size="small"
                                class="mr-2"
                              >
                                {{ expanded ? 'mdi-folder-open' : 'mdi-folder' }}
                              </v-icon>
                              <span class="text-subtitle-1">
                                {{ seccion.NombreSeccion }} {{ (seccion.items.length === 0) ? '(Sin items)' : '' }}
                              </span>
                            </v-col>
                            <v-col cols="3" class="text-right">
                              <v-chip 
                                :color="getItemsCountForSection(seccion.SeccionID, optionIndex) > 0 ? 'secondary' : 'grey-lighten-1'"
                                size="x-small"
                              >
                                {{ getItemsCountForSection(seccion.SeccionID, optionIndex) }}/{{ seccion.items.length }}
                              </v-chip>
                            </v-col>
                          </v-row>
                        </template>
                      </v-expansion-panel-title>

                      <!-- Items -->
                      <v-expansion-panel-text>
                        <!-- Botón seleccionar todos de la sección -->
                        <div class="mb-3 mt-2">
                          <v-btn 
                            :color="getItemsCountForSection(seccion.SeccionID, optionIndex) > 0 ? 'secondary' : 'grey'"
                            variant="text"
                            size="x-small"
                            @click="toggleAllItemsForSection(seccion.SeccionID, seccion.items, optionIndex)"
                          >
                            <v-icon start size="small">mdi-checkbox-multiple-marked</v-icon>
                            Seleccionar todos de esta sección
                          </v-btn>
                        </div>
                        
                        <v-row class="pa-2">
                          <v-col
                            v-for="item in seccion.items"
                            :key="item.ItemID"
                            cols="12"
                            md="6"
                            lg="4"
                          >
                            <v-card
                              :class="['item-card', { 'selected': isItemSelected(item.ItemID, optionIndex) }]"
                              elevation="2"
                              @click="toggleItem(item, optionIndex)"
                            >
                              <v-card-text>
                                <div class="d-flex align-center justify-space-between mb-2">
                                  <v-icon v-if="isItemSelected(item.ItemID, optionIndex)" color="success">
                                    mdi-check-decagram
                                  </v-icon>
                                  <v-icon v-else color="grey">
                                    mdi-decagram-outline
                                  </v-icon>
                                  <v-chip
                                    color="primary"
                                    size="small"
                                    variant="flat"
                                  >
                                    {{ formatCurrency(item.PrecioUnitario) }}
                                  </v-chip>
                                </div>
                                <p class="text-subtitle-2 font-weight-bold mb-1">
                                  {{ item.NombreItem }}
                                </p>
                                <p v-if="item.Descripcion" class="text-caption text-grey-darken-1 mb-0">
                                  {{ item.Descripcion }}
                                </p>
                                <!-- Cantidad: solo visible cuando el ítem está seleccionado -->
                                <div
                                  v-if="isItemSelected(item.ItemID, optionIndex)"
                                  class="mt-3 d-flex align-center gap-2"
                                  @click.stop
                                >
                                  <v-btn
                                    icon="mdi-minus"
                                    size="x-small"
                                    variant="outlined"
                                    color="primary"
                                    @click.stop="updateItemCantidad(item.ItemID, optionIndex, getItemCantidad(item.ItemID, optionIndex) - 1)"
                                  />
                                  <v-text-field
                                    :model-value="getItemCantidad(item.ItemID, optionIndex)"
                                    @update:model-value="updateItemCantidad(item.ItemID, optionIndex, $event)"
                                    type="number"
                                    min="1"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    class="cantidad-field"
                                    @click.stop
                                  />
                                  <v-btn
                                    icon="mdi-plus"
                                    size="x-small"
                                    variant="outlined"
                                    color="primary"
                                    @click.stop="updateItemCantidad(item.ItemID, optionIndex, getItemCantidad(item.ItemID, optionIndex) + 1)"
                                  />
                                  <span class="text-caption text-grey-darken-1 ml-1">
                                    = {{ formatCurrency(item.PrecioUnitario * getItemCantidad(item.ItemID, optionIndex)) }}
                                  </span>
                                </div>
                              </v-card-text>
                            </v-card>
                          </v-col>
                        </v-row>

                        <!-- Gestión de Imágenes de la Sección -->
                        <v-card class="mt-4" variant="outlined" v-if="getItemsCountForSection(seccion.SeccionID, optionIndex) > 0">
                          <v-card-title class="text-subtitle-1 bg-blue-grey-lighten-5">
                            <v-icon start color="primary">mdi-image-multiple</v-icon>
                            Referencia de Imágenes - {{ seccion.NombreSeccion }}
                          </v-card-title>
                          <v-card-text class="pa-4">
                            <div v-if="getSectionImages(seccion.SeccionID, optionIndex).length > 0" class="mb-4">
                              <p class="text-subtitle-2 font-weight-bold mb-3">Imágenes seleccionadas:</p>
                              <v-row>
                                <v-col cols="6" sm="4" md="4" lg="4"
                                  v-for="(imagen, index) in getSectionImages(seccion.SeccionID, optionIndex)" 
                                  :key="`img-${seccion.SeccionID}-${index}`"
                                > 
                                  <v-card class="image-preview-card" elevation="2"> 
                                    <v-img
                                      :src="getImagePath(imagen.image)"
                                      height="120"
                                      cover
                                      class="rounded"
                                    >
                                      <v-btn 
                                        size="x-small" 
                                        icon="mdi-close" 
                                        color="error" 
                                        variant="elevated"
                                        density="comfortable"
                                        class="remove-image-btn"
                                        @click="removeImageFromSection(seccion.SeccionID, index, optionIndex)"
                                      />
                                    </v-img>
                                    <v-card-subtitle class="text-caption pa-2 text-center">
                                      {{ imagen.title }}
                                    </v-card-subtitle>
                                  </v-card>
                                </v-col>
                                <v-col cols="6" sm="4" md="4" lg="4">
                                <div v-if="loaderImagen">
                                  <v-skeleton-loader
                                    type="image@1"
                                    :loading="true"
                                    class="mb-4"
                                    height="75"
                                  />
                                </div>
                                </v-col>
                              </v-row>
                            </div>

                            <div class="image-gallery">
                              <!-- Botón para agregar más imágenes -->
                              <label class="add-image-item">
                                <v-icon large color="#dbdbdb">mdi-plus-circle-outline</v-icon>
                                <span class="add-text">agregar referencia</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style="display: none"
                                    @change="handleImageUpload($event, seccion.SeccionID, optionIndex)"
                                  />
                                </label>
                              </div>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Resumen de la opción -->
            <v-card class="mt-6" elevation="2" color="grey-lighten-5">
              <v-card-title class="text-h6">
                <v-icon start color="primary">mdi-calculator</v-icon>
                Resumen de {{ option.nombre }}
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="6" md="3">
                    <div class="text-caption text-grey-darken-1">Items seleccionados</div>
                    <div class="text-h6 font-weight-bold text-primary">
                      {{ option.selectedItems.length }}
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="text-caption text-grey-darken-1">Subtotal</div>
                    <div class="text-h6 font-weight-bold">
                      {{ formatCurrency(calculateSubtotalForOption(optionIndex)) }}
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="text-caption text-grey-darken-1">Descuento ({{ discountPercent }}%)</div>
                    <div class="text-h6 font-weight-bold text-red">
                      -{{ formatCurrency(calculateDiscountForOption(optionIndex)) }}
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="text-caption text-grey-darken-1">Total</div>
                    <div class="text-h5 font-weight-bold text-success">
                      {{ formatCurrency(calculateTotalForOption(optionIndex)) }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-window-item>
      </v-window>
    </v-card>

    <!-- Configuración General -->
    <v-card class="mb-6" elevation="4">
      <v-card-title class="text-h5 pa-6 bg-grey-lighten-4">
        <v-icon start color="primary">mdi-cog</v-icon>
        Configuración General
      </v-card-title>
      <v-card-text class="pa-6">
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="discountPercent"
              label="Descuento General (%)"
              type="number"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-percent"
              :min="0"
              :max="100"
            />
          </v-col>
          <v-col cols="12" md="12">
            <v-textarea
              v-model="observations"
              label="Observaciones Generales"
              variant="outlined"
              density="comfortable"
              rows="3"
              prepend-inner-icon="mdi-note-text"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Botones de Acción -->
    <v-card class="summary-fixed-card" elevation="12">
      <v-card-text class="pa-6">
        <v-row align="center">
          <v-col cols="12" md="8">
            <div class="text-h6 mb-2">
              <v-icon start color="primary">mdi-file-document-multiple</v-icon>
              {{ options.length }} Opción{{ options.length > 1 ? 'es' : '' }} creada{{ options.length > 1 ? 's' : '' }}
            </div>
            <div class="text-caption text-grey-darken-1">
              Total de items: {{ getTotalItemsCount() }}
            </div>
          </v-col>
          <v-col cols="12" md="12" class="text-right">
            <v-card-actions class="pa-6 bg-grey-lighten-4">
              <v-spacer />
              <v-btn
                color="primary"
                size="x-large"
                prepend-icon="mdi-content-save"
                :disabled="!canSave || saving"
                :loading="saving"
                @click="saveQuotation"
                min-width="300"
              >
                {{ saving ? 'Guardando...' : 'Guardar Opciones' }}
              </v-btn>
              <v-spacer /> 
              <v-btn
                color="primary"
                size="x-large"
                prepend-icon="mdi-content-save"
                :disabled="!canSave || saving"
                :loading="saving"
                @click="generatePDF"
                min-width="300"
              >
                {{ saving ? 'Imprimiendo...' : 'Imprimir Cotización' }}
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Toast de notificaciones -->
    <v-snackbar
      v-model="toast.show"
      :color="toast.type"
      timeout="3000"
      location="top"
    >
      {{ toast.message }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { generatePDFOptions as generatePDFUtilOptions, type PdfData } from '@/utils/pdfGenerator-opciones'
import { GalleryItem } from '@/interfaces/gallery.interface'
import { getImagePath, loadImageBase64 } from '@/utils/constantes'

defineOptions({
  name: 'cotizacionNew'
})

const router = useRouter()
const store = useStore()

// ============================================================================
// INTERFACES
// ============================================================================

interface Option {
  nombre: string
  descripcion: string
  selectedItems: any[]
  search: string
  expandedPanels: number[]
  expandedSections: { [key: number]: number[] }
  sectionImagesMap: Record<number, GalleryItem[]>  // ← NUEVO: Cada opción tiene su propio mapa
}

interface Seccion {
  SeccionID: number
  ServicioID: number
  NombreSeccion: string
  Descripcion: string
  ImagenesSeccion: string
  Orden: number
  items: Item[]
}

interface Item {
  ItemID: number
  SeccionID: number
  NombreItem: string
  ImagenesSeccion: string
  PrecioUnitario: any
  Descripcion: string
  Imagenes: string[]
  Orden: number
  NombreSeccion?: string
  NombreServicio?: string
  Cantidad?: number
}

// ============================================================================
// ESTADO
// ============================================================================

const nombreCliente = ref('')
const tipoEventoID = ref<number | null>(1)
const eventDate = ref(new Date().toISOString().split('T')[0])
const guestCount = ref<number | null>(50)
const discountPercent = ref(0)
const observations = ref('')
const saving = ref(false)
const loadingCatalog = ref(true)
interface ImageItem {
  file: File;
  name: string;
  preview: string;
}

const loaderGallery = ref(false)
const selectedImages = ref<ImageItem[]>([]);
const fileInput = ref(null);
const loaderImagen = ref(false)

const currentOption = ref<Option>({
  nombre: 'Opción 1',
  descripcion: '',
  selectedItems: [],
  search: '',
  expandedPanels: [],
  expandedSections: {},
  sectionImagesMap: {}  // ← NUEVO
})

// Galería
const gallery = ref<GalleryItem[]>([])
const tab = ref<string>('COT')
const loadSkeleton = ref(false)
const page = ref(1)
const itemsPerPage = ref(30)

// Sistema de opciones
const options = ref<Option[]>([
  {
    nombre: 'Opción 1',
    descripcion: '',
    selectedItems: [],
    search: '',
    expandedPanels: [],
    expandedSections: {},
    sectionImagesMap: {}  // ← NUEVO
  }
])
const selectedOptionTab = ref(0)

// Catálogo
const catalogoServicios = ref<any[]>([])
const tiposEvento = ref<any[]>([])
const payments = ref([{ amount: 0 }])

// Toast
const toast = ref({
  show: false,
  message: '',
  type: 'success'
})

// ============================================================================
// COMPUTED
// ============================================================================

const canSave = computed(() => {
  return nombreCliente.value && 
         tipoEventoID.value && 
         eventDate.value && 
         guestCount.value &&
         options.value.some(opt => opt.selectedItems.length > 0)
})

const gallerys = computed(() => store.state.Gallery.galleryList)

const subtotal = computed(() => {
  return options.value.reduce((sum, opt) => {
    return sum + opt.selectedItems.reduce((itemSum, item) =>
      itemSum + (Number.parseFloat(item.PrecioUnitario) || 0) * (item.Cantidad || 1), 0)
  }, 0)
})

const discountAmount = computed(() => {
  return (subtotal.value * discountPercent.value) / 100
})

const totalWithDiscount = computed(() => {
  return subtotal.value - discountAmount.value
})

const totalPayments = computed(() => {
  return payments.value.reduce((sum, p) => sum + (p.amount || 0), 0)
})

const pendingAmount = computed(() => {
  return totalWithDiscount.value - totalPayments.value
})

// ============================================================================
// MÉTODOS DE OPCIONES
// ============================================================================

function addOption() {
  const nextNumber = options.value.length + 1
  options.value.push({
    nombre: `Opción ${nextNumber}`,
    descripcion: '',
    selectedItems: [],
    search: '',
    expandedPanels: [],
    expandedSections: {},
    sectionImagesMap: {}  // ← NUEVO
  })
  selectedOptionTab.value = options.value.length - 1
}

function removeOption(index: number) {
  if (options.value.length === 1) {
    showToast('Debe existir al menos una opción', 'warning')
    return
  }
  options.value.splice(index, 1)
  if (selectedOptionTab.value >= options.value.length) {
    selectedOptionTab.value = options.value.length - 1
  }
}

// ============================================================================
// MÉTODOS DE CATÁLOGO
// ============================================================================

function getCatalogoFiltrado(optionIndex: number) {
  const searchTerm = options.value[optionIndex].search?.toLowerCase() || ''
  
  if (!searchTerm) {
    return catalogoServicios.value
  }

  return catalogoServicios.value.map(servicio => ({
    ...servicio,
    secciones: servicio.secciones.map((seccion: any) => ({
      ...seccion,
      items: seccion.items.filter((item: any) => 
        (item.NombreItem?.toLowerCase().includes(searchTerm) ||
        seccion.NombreSeccion?.toLowerCase().includes(searchTerm) ||
        servicio.NombreServicio?.toLowerCase().includes(searchTerm)) && item.Activo === 1
      )
    })).filter((seccion: any) => seccion.items.length > 0 && seccion.Activo === 1)
  })).filter(servicio => servicio.secciones.length > 0 && servicio.Activo === 1)
}

function changeSearchForOption(optionIndex: number) {
  const option = options.value[optionIndex]
  if (option.search && option.search.trim() !== '') {
    const filtrado = getCatalogoFiltrado(optionIndex)
    option.expandedPanels = filtrado.map((_, index) => index)
    filtrado.forEach((servicio: any) => {
      option.expandedSections[servicio.ServicioID] = servicio.secciones.map((_: any, index: number) => index)
    })
  } else {
    option.expandedPanels = []
    option.expandedSections = {}
  }
}

// ============================================================================
// MÉTODOS DE ITEMS
// ============================================================================

function isItemSelected(itemID: number, optionIndex: number): boolean {
  return options.value[optionIndex].selectedItems.some((i: any) => i.ItemID === itemID)
}

function toggleItem(item: Item, optionIndex: number) {
  const currentOption = options.value[optionIndex]
  const index = currentOption.selectedItems.findIndex(i => i.ItemID === item.ItemID)
  
  if (index > -1) {
    currentOption.selectedItems.splice(index, 1)
  } else {
    const seccion = findSeccionForItem(item.ItemID)
    const servicio = findServicioForSeccion(seccion?.SeccionID)
    const sectionImages = currentOption.sectionImagesMap[seccion?.SeccionID ?? 0] ?? []
    
    currentOption.selectedItems.push({
      ...item,
      Cantidad: 1,
      ImagenesSeccion: JSON.stringify(sectionImages.map((img: GalleryItem) => img.image)),
      NombreSeccion: seccion?.NombreSeccion,
      NombreServicio: servicio?.NombreServicio
    })
  }
}

function findSeccionForItem(itemID: number): Seccion | undefined {
  for (const servicio of catalogoServicios.value) {
    for (const seccion of servicio.secciones) {
      if (seccion.items.some((i: Item) => i.ItemID === itemID)) {
        return seccion
      }
    }
  }
  return undefined
}

function getItemsCountForService(servicioID: number, optionIndex: number): number {
  return options.value[optionIndex].selectedItems.filter((item: any) => 
    item.ServicioID === servicioID
  ).length
}

function getItemsCountForSection(seccionID: number, optionIndex: number): number {
  return options.value[optionIndex].selectedItems.filter((item: any) => 
    item.SeccionID === seccionID
  ).length
}

function getLengthItemsForService(servicioID: number, optionIndex: number): number {
  const servicio = catalogoServicios.value.find((s: any) => s.ServicioID === servicioID)
  if (!servicio) return 0
  return servicio.secciones.reduce((total: number, seccion: any) => total + seccion.items.length, 0)
}

function getTotalItemsCount(): number {
  return options.value.reduce((total, opt) => total + opt.selectedItems.length, 0)
}

function getItemCantidad(itemID: number, optionIndex: number): number {
  const item = options.value[optionIndex].selectedItems.find((i: any) => i.ItemID === itemID)
  return item?.Cantidad ?? 1
}

function updateItemCantidad(itemID: number, optionIndex: number, value: string | number) {
  const item = options.value[optionIndex].selectedItems.find((i: any) => i.ItemID === itemID)
  if (item) {
    item.Cantidad = Math.max(1, parseInt(String(value)) || 1)
  }
}

// ============================================================================
// MÉTODOS DE GESTIÓN DE IMÁGENES (CORREGIDOS)
// ============================================================================

function getSectionImages(seccionID: number, optionIndex: number): GalleryItem[] {
  const currentOption = options.value[optionIndex]
  return currentOption.sectionImagesMap[seccionID] || []
}

function updateSectionImages(seccionID: number, images: GalleryItem[], optionIndex: number) {
  const currentOption = options.value[optionIndex]
  currentOption.sectionImagesMap[seccionID] = [...images]
  
  // Actualizar items seleccionados de esta opción
  currentOption.selectedItems.forEach(item => {
    if (item.SeccionID === seccionID) {
      item.ImagenesSeccion = JSON.stringify(images.map((img: GalleryItem) => img.image))
    }
  })
}

// ============================================================================
// CÁLCULOS
// ============================================================================

function calculateSubtotalForOption(optionIndex: number): number {
  return options.value[optionIndex].selectedItems.reduce((sum: number, item: any) =>
    sum + parseFloat(item.PrecioUnitario || 0) * (item.Cantidad || 1), 0
  )
}

function calculateDiscountForOption(optionIndex: number): number {
  const subtotal = calculateSubtotalForOption(optionIndex)
  return subtotal * (discountPercent.value / 100)
}

function calculateTotalForOption(optionIndex: number): number {
  const subtotal = calculateSubtotalForOption(optionIndex)
  const discount = calculateDiscountForOption(optionIndex)
  return subtotal - discount
}

// ============================================================================
// CARGA DE DATOS
// ============================================================================

async function loadCatalog() {
  try {
    loadingCatalog.value = true
    await store.dispatch('Cotizacion/fetchCatalogo')
    
    const { servicios, secciones, items } = store.state.Cotizacion.Catalogo.data.data
    await store.dispatch('Cotizacion/getTiposEvento')
    tiposEvento.value = store.state.Cotizacion.TiposEvento.data.data
    
    const seccionesMap = new Map<number, Seccion>()
    // Filtrar secciones e items activos
    secciones.forEach((sec: any) => {
      if (sec.Activo === 0) return
      seccionesMap.set(sec.SeccionID, {
        ...sec,
        ImagenesSeccion: sec.ImagenesSeccion ? JSON.parse(sec.ImagenesSeccion) : [],
        items: items.filter((i: any) => i.SeccionID === sec.SeccionID && i.Activo === 1)
          .map((i: any) => ({
            ...i,
            Imagenes: i.Imagenes ? JSON.parse(i.Imagenes) : []
          }))
      })
    })
    // Filtrar servicios activos
    catalogoServicios.value = servicios.filter((serv: any) => serv.Activo === 1).map((serv: any) => ({
      ...serv,
      secciones: Array.from(seccionesMap.values())
        .filter(sec => sec.ServicioID === serv.ServicioID)
    }))

    loadingCatalog.value = false
  } catch (error) {
    console.error('Error cargando catálogo:', error)
    showToast('Error cargando el catálogo', 'error')
  } finally {
    loadingCatalog.value = false
  }
}

async function loadGallery() {
  loadSkeleton.value = true
  await store.dispatch('Gallery/fetchGallery', {
    params: {
      page: page.value,
      limit: itemsPerPage.value,
      type: tab.value,
    }
  })
  gallery.value = gallerys.value?.data.items ?? []
  loadSkeleton.value = false
}

function onTipoEventoChange() {
  // Lógica adicional si es necesaria
}

// ============================================================================
// GUARDADO Y PDF
// ============================================================================
async function guardarGaleria({ data, imageFile }: { data: GalleryItem; imageFile: File | null }) {
  loaderGallery.value = true
  const formData = new FormData()
  formData.append('adicional_class', data.adicional_class ?? '')
  formData.append('category', data.category)
  formData.append('description', data.description)
  formData.append('filter', data.filter ?? '')
  formData.append('status', data.status ?? 'A')
  formData.append('title', data.title)
  formData.append('type', data.type)
  if (imageFile) {
    formData.append('image', imageFile)
  } else if (data.image && !data.image.startsWith('http')) {
    try {
      const image = await urlToFile(getImagePath(data.image), data.image)
      formData.append('image', image)
    } catch (error) {
      console.error('Error convirtiendo imagen existente en archivo:', error)
    }
  }
  await store.dispatch('Gallery/postGallery', formData)
  // loaderGallery.value = false
  // snackbar.value = true
  // if (statusInsertGallery.value?.success) {
  //   dialogEdit.value = false
  //   cargarGaleria()
  // }
}
async function urlToFile(url: string, filename: string, mimeType = 'image/jpeg'): Promise<File> {
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
  })
  const blob = await response.blob()
  return new File([blob], filename.split('/').pop() || 'image.jpg', { type: mimeType })
}
async function saveQuotation() {
  if (!canSave.value) {
    showToast('Por favor completa todos los campos requeridos', 'warning')
    return
  }

  try {
    saving.value = true

    const pdfData: PdfData = {
      nombreCliente: nombreCliente.value,
      tipoEvento: tiposEvento.value.find(t => t.TipoEventoID === tipoEventoID.value)?.NombreTipo || '',
      selectedItems: options.value,
      catalogoServicios: catalogoServicios.value,
      eventDate: eventDate.value,
      guestCount: guestCount.value || 0,
      discountPercent: discountPercent.value,
      discountAmount: discountAmount.value,
      subtotal: subtotal.value,
      totalWithDiscount: totalWithDiscount.value,
      payments: payments.value,
      pendingAmount: totalWithDiscount.value,
      observations: '',
      opciones: options.value.map((option, index) => ({
        numero: index + 1,
        nombre: option.nombre,
        descripcion: option.descripcion,
        selectedItems: option.selectedItems.map((item: any) => ({
          ...item,
          ImagenesSeccion: item.ImagenesSeccion ? JSON.parse(item.ImagenesSeccion) : [] //nombre de la imagen ejemplo "['test.png']"
        })),
        subtotal: calculateSubtotalForOption(index),
        discountAmount: calculateDiscountForOption(index),
        totalWithDiscount: calculateTotalForOption(index)
      })),
      backgroundRespage: await loadImageBase64('/pdf/bk_pdf.png'),
      backgroundHeader: await loadImageBase64('/pdf/header.png'),
      signatureImage: await loadImageBase64('/pdf/firma.png')
    }
    
    // Preparar datos para cada opción
    const opcionesData = options.value.map((option, index) => ({
      numeroOpcion: index + 1,
      nombreOpcion: option.nombre,
      descripcion: option.descripcion,
      items: option.selectedItems.map(item => ({
        itemID: item.ItemID,
        cantidad: item.Cantidad || 1,
        observaciones: ''
      }))
    }))

    const payload = {
      NombreCliente: nombreCliente.value,
      TipoEventoID: tipoEventoID.value,
      FechaEvento: eventDate.value,
      NumeroInvitados: guestCount.value,
      PorcentajeDescuento: discountPercent.value,
      Observaciones: observations.value,
      DiasExpiracion: 7,
      Opciones: opcionesData,
      PdfData: JSON.stringify(pdfData)
    }

    await store.dispatch('Cotizacion/createCotizacionConOpciones', payload)
    const result = store.state.Cotizacion.result
    
    if (result.success) {
      showToast(result.message)
      resetForm() 
      gotoOptions()
    } else {
      showToast(result.message, 'error')
    }
  } catch (error: any) {
    console.error('Error guardando cotización:', error)
    showToast(error.response?.data?.message || 'Error al guardar la cotización', 'error')
  } finally {
    saving.value = false
  }
}

async function generatePDF() {
  if (!canSave.value) {
    showToast('Por favor completa todos los campos requeridos', 'error')
    return
  }

  saving.value = true
  try {
    const pdfData: PdfData = {
      nombreCliente: nombreCliente.value,
      tipoEvento: tiposEvento.value.find(t => t.TipoEventoID === tipoEventoID.value)?.NombreTipo || '',
      eventDate: eventDate.value,
      guestCount: guestCount.value || 0,
      discountPercent: discountPercent.value,
      observations: observations.value,
      opciones: options.value.map((option, index) => ({
        numero: index + 1,
        nombre: option.nombre,
        descripcion: option.descripcion,
        selectedItems: option.selectedItems,
        subtotal: calculateSubtotalForOption(index),
        discountAmount: calculateDiscountForOption(index),
        totalWithDiscount: calculateTotalForOption(index)
      })),
      catalogoServicios: catalogoServicios.value,
      payments: [],
      pendingAmount: 0,
      selectedItems: [],
      subtotal: 0,
      discountAmount: 0,
      totalWithDiscount: 0,
      backgroundRespage: await loadImageBase64('/pdf/bk_pdf.png'),
      backgroundHeader: await loadImageBase64('/pdf/header.png'),
      signatureImage: await loadImageBase64('/pdf/firma.png')
    }
    
    await generatePDFUtilOptions(pdfData)
    showToast('✅ PDF generado exitosamente')
  } catch (error) {
    console.error('Error generando PDF:', error)
    showToast('Error al generar el PDF', 'error')
  } finally {
    saving.value = false
  }
}

// ============================================================================
// UTILIDADES
// ============================================================================

function resetForm() {
  nombreCliente.value = ''
  tipoEventoID.value = 1
  eventDate.value = new Date().toISOString().split('T')[0]
  guestCount.value = 50
  discountPercent.value = 0
  observations.value = ''
  options.value = [{
    nombre: 'Opción 1',
    descripcion: '',
    selectedItems: [],
    search: '',
    expandedPanels: [],
    expandedSections: {},
    sectionImagesMap: {}
  }]
  selectedOptionTab.value = 0
}

function formatCurrency(value: number | string) {
  if (typeof value === 'string') {
    value = Number.parseFloat(value)
  }
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value || 0)
}

function goToList() {
  router.push('/cotizaciones/list')
}

function gotoOptions() {
  router.push({ path: '/cotizaciones/opciones', query: { from: 'new' } }) // que mande una bandera para recargar la vista cuando venga de aqui
}

function showToast(message: string, type = 'success') {
  toast.value = { show: true, message, type }
}

function toggleAllItemsForService(servicioID: number, optionIndex: number) {
  const servicio = catalogoServicios.value.find(s => s.ServicioID === servicioID)
  if (!servicio) return

  const currentOption = options.value[optionIndex]
  const allItems: Item[] = []
  
  servicio.secciones.forEach((seccion: any) => {
    const sectionImages = currentOption.sectionImagesMap[seccion.SeccionID] ?? []
    seccion.items.forEach((item: any) => {
      allItems.push({
        ...item,
        ImagenesSeccion: JSON.stringify(sectionImages.map((img: GalleryItem) => img.image)),
        NombreSeccion: seccion.NombreSeccion,
        NombreServicio: servicio.NombreServicio
      })
    })
  })
  
  const allSelected = allItems.every(item => isItemSelected(item.ItemID, optionIndex))
  
  if (allSelected) {
    allItems.forEach(item => {
      const index = currentOption.selectedItems.findIndex(i => i.ItemID === item.ItemID)
      if (index > -1) {
        currentOption.selectedItems.splice(index, 1)
      }
    })
  } else {
    allItems.forEach(item => {
      if (!isItemSelected(item.ItemID, optionIndex)) {
        currentOption.selectedItems.push({ ...item, Cantidad: 1 })
      }
    })
  }
}

function toggleAllItemsForSection(seccionID: number, items: Item[], optionIndex: number) {
  const seccion = findSeccionById(seccionID)
  if (!seccion) return
  
  const servicio = findServicioForSeccion(seccionID)
  const currentOption = options.value[optionIndex]
  const allSelected = items.every(item => isItemSelected(item.ItemID, optionIndex))
  
  if (allSelected) {
    items.forEach(item => {
      const index = currentOption.selectedItems.findIndex(i => i.ItemID === item.ItemID)
      if (index > -1) {
        currentOption.selectedItems.splice(index, 1)
      }
    })
  } else {
    items.forEach(item => {
      if (!isItemSelected(item.ItemID, optionIndex)) {
        const sectionImages = currentOption.sectionImagesMap[seccion.SeccionID] ?? []
        currentOption.selectedItems.push({
          ...item,
          Cantidad: 1,
          ImagenesSeccion: JSON.stringify(sectionImages.map((img: GalleryItem) => img.image)),
          NombreSeccion: seccion.NombreSeccion,
          NombreServicio: servicio?.NombreServicio
        })
      }
    })
  }
}

function findSeccionById(seccionID: number): Seccion | undefined {
  for (const servicio of catalogoServicios.value) {
    const seccion = servicio.secciones.find((s: any) => s.SeccionID === seccionID)
    if (seccion) return seccion
  }
  return undefined
}

function findServicioForSeccion(seccionID: number | undefined): any {
  if (!seccionID) return undefined
  return catalogoServicios.value.find((s: any) => 
    s.secciones.some((sec: any) => sec.SeccionID === seccionID)
  )
}

/**
 * Maneja la subida de imágenes a una sección
 */
async function handleImageUpload(event: Event, seccionID: number, optionIndex: number) {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (!files || files.length === 0) return

  const currentOption = options.value[optionIndex]
  
  // Inicializar el array si no existe
  if (!currentOption.sectionImagesMap[seccionID]) {
    currentOption.sectionImagesMap[seccionID] = []
  }

  loaderGallery.value = true

  for (const file of Array.from(files)) {
    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      console.warn(`${file.name} no es una imagen válida`)
      showToast(`${file.name} no es una imagen válida`, 'error')
      continue
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      console.warn(`${file.name} excede el tamaño máximo de 5MB`)
      showToast(`${file.name} excede el tamaño máximo de 5MB`, 'error')
      continue
    }

    try {
      loaderImagen.value = true
      // Subir la imagen al servidor usando la función existente
      const formData = new FormData()
      formData.append('adicional_class', '')
      formData.append('category', 'cotizacion')
      formData.append('description', `Imagen de sección ${seccionID}`)
      formData.append('filter', '')
      formData.append('status', 'A')
      formData.append('title', file.name)
      formData.append('type', 'COT')
      formData.append('image', file)
      formData.append('isSaveInDB', '0') // ← Cambiado a '0' para que no se guarde en la base de datos
      await store.dispatch('Gallery/postGallery', formData)
      const statusInsertGallery = store.state.Gallery.statusInsertGallery

      if (statusInsertGallery?.success) {
        // Obtener el nombre del archivo guardado desde la respuesta
        const savedImageName = statusInsertGallery.data?.image || file.name
        
        // Agregar la imagen al mapa de la sección con el nombre del archivo
        currentOption.sectionImagesMap[seccionID].push({
          image: normalizeFileName(savedImageName), // ← Nombre del archivo en lugar de base64
          title: normalizeFileName(file.name),
          id: statusInsertGallery.data?.id || 0,
          category: 'cotizacion',
          type: 'COT',
          filter: '',
          adicional_class: '',
          description: `Imagen de sección ${seccionID}`,
          status: 'A'
        })

        // Actualizar los items seleccionados de esta sección
        updateSelectedItemsWithImages(seccionID, optionIndex)
        
        showToast(`✅ Imagen ${file.name} cargada exitosamente`, 'success')
      } else {
        showToast(`Error al subir ${file.name}`, 'error')
      }
    } catch (error) {
      console.error(`Error al procesar el archivo ${file.name}:`, error)
      showToast(`Error al cargar ${file.name}`, 'error')
    }
  }

  loaderGallery.value = false

  // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
  target.value = ''
  loaderImagen.value = false
}
 
 function normalizeFileName(fileName: string): string {
  return fileName.replace(/[^a-zA-Z0-9_\.\-]/g, '_')
}
/**
 * Elimina una imagen de una sección
 */
function removeImageFromSection(seccionID: number, imageIndex: number, optionIndex: number) {
  const currentOption = options.value[optionIndex]
  
  if (!currentOption.sectionImagesMap[seccionID]) return

  // Eliminar la imagen del array
  currentOption.sectionImagesMap[seccionID].splice(imageIndex, 1)

  // Actualizar los items seleccionados de esta sección
  updateSelectedItemsWithImages(seccionID, optionIndex)
}

/**
 * Actualiza las imágenes en los items seleccionados de una sección
 */
function updateSelectedItemsWithImages(seccionID: number, optionIndex: number) {
  const currentOption = options.value[optionIndex]
  const sectionImages = currentOption.sectionImagesMap[seccionID] ?? []
  
  // Actualizar todos los items seleccionados de esta sección
  currentOption.selectedItems.forEach((item: Item) => {
    if (item.SeccionID === seccionID) {
      item.ImagenesSeccion = JSON.stringify(sectionImages.map((img: GalleryItem) => img.image))
    }
  })
}

// ============================================================================
// LIFECYCLE
// ============================================================================

onMounted(async () => {
  await loadCatalog()
  await loadGallery()
})
</script>

<style scoped>
.quotation-options-container {
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  min-height: 100vh;
  padding-bottom: 100px;
}

.header-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

.item-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.item-card.selected {
  border-color: #4caf50;
  background-color: rgba(76, 175, 80, 0.05);
}

.summary-fixed-card {
  position: sticky;
  bottom: 0;
  z-index: 999;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15) !important;
}

.image-preview-card {
  position: relative;
}

.remove-image-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}

.image-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 0;
  min-height: 180px;
}

.image-item {
  position: relative;
  width: 160px;
  height: 160px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.delete-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.add-image-item {
  width: 160px;
  height: 160px;
  border: 2px dashed #dbdbdb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
  flex-shrink: 0;
}

.add-image-item:hover {
  background: #ffffff;
  border-color: #a3a3a3;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.2);
}

.add-text {
  margin-top: 8px;
  color: #dbdbdb;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.cantidad-field {
  width: 64px;
  flex-shrink: 0;
}

.cantidad-field :deep(input) {
  text-align: center;
}
</style>